import * as React from 'react';
import { useState } from 'react';
import {
  Button,
  CssBaseline,
  TextField,
  FormControlLabel,
  Checkbox,
  Link,
  Box,
  Typography,
  Snackbar,
  createTheme,
  ThemeProvider,
  InputAdornment,
  IconButton,
  Fade,
} from '@mui/material';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { styled } from '@mui/system';
import axios from 'axios';
import User from '../data/User';
import API_URL from '../apiConfig';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import signInBg from '../assets/signin.JPG';

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#e88ba9',
    },
    text: {
      primary: '#5c434a',
      secondary: '#8c7077',
    },
  },
  typography: {
    fontFamily: '"Outfit", "Inter", sans-serif',
    h3: {
      fontFamily: '"Playfair Display", serif',
      color: '#d46a89',
    }
  },
});

const BakeryButton = styled(Button)({
  background: '#e88ba9',
  borderRadius: '30px',
  border: 0,
  color: 'white',
  height: 54,
  padding: '0 30px',
  boxShadow: '0 8px 16px rgba(232, 139, 169, 0.4)',
  fontWeight: 700,
  fontSize: '1rem',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  marginTop: '24px',
  marginBottom: '24px',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 20px rgba(212, 106, 137, 0.6)',
    background: '#d46a89',
  },
  '&:active': {
    transform: 'translateY(0)',
  }
});

const SoftTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
    borderRadius: '16px',
    transition: 'all 0.3s ease',
    color: '#5c434a',
    backdropFilter: 'blur(10px)',
    '& fieldset': {
      borderColor: 'rgba(232, 139, 169, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: '#e88ba9',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#e88ba9',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#8c7077',
    '&.Mui-focused': {
      color: '#e88ba9',
    }
  },
  '& .MuiSvgIcon-root': {
    color: '#8c7077',
  }
});

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const redirectPath = queryParams.get('redirect');

  const [email, setemail] = useState("");
  const [password, setpassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleClick = async (e) => {
    e.preventDefault();

    const isvalid = email.length > 0 && password.length > 0;
    if (isvalid) {
      try {
        const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
        if (response.status === 200) {
          User.instance.setToken(response.data.token);
          if (redirectPath === 'checkout') {
            navigate('/checkout');
          } else {
            navigate('/profile');
          }
        } else {
          setError("Invalid email or password");
        }
      } catch (error) {
        console.error('Login failed:', error);
        setError(error.response?.data?.message || "Invalid email or password");
      }
    } else {
      setError("Please fill in all fields.");
    }
  };

  const handleclose = (e, reason) => {
    if (reason === 'clickaway') return;
    setError("");
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      {/* Background Layer with Dark Smoke/Blur overlay */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          paddingX: 2,
          pt: '80px',
          pb: '40px',
        }}
      >
        <CssBaseline />

        {/* Background Image Layer */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url("${signInBg}")`,
            backgroundSize: '100% 100%',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'blur(3px)', // Blurred cake image 
            zIndex: -2
          }}
        />

        {/* White Gradiant Overlay Layer */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.95) 100%)', // White smoke
            zIndex: -1
          }}
        />

        <Fade in={true} timeout={1000}>
          <Box
            sx={{
              p: { xs: 4, md: 5 },
              width: '100%',
              maxWidth: '450px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              zIndex: 1
            }}
          >
            <Typography component="h1" variant="h3" sx={{ fontWeight: 800, mb: 1, textAlign: 'center' }}>
              Slice of Sweet
            </Typography>
            <Typography variant="body1" sx={{ color: '#8c7077', mb: 4, textAlign: 'center', fontWeight: 500 }}>
              Welcome back! Delicious treats are waiting for you.
            </Typography>

            <Box component="form" noValidate onSubmit={handleClick} sx={{ width: '100%' }}>
              <SoftTextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setemail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <SoftTextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setpassword(e.target.value)}
                sx={{ mb: 2, mt: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#8c7077' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 1 }}>
                <FormControlLabel
                  control={<Checkbox value="remember" sx={{ color: '#8c7077', '&.Mui-checked': { color: '#e88ba9' } }} />}
                  label={<Typography sx={{ color: '#8c7077', fontSize: '0.9rem', fontWeight: 600 }}>Remember me</Typography>}
                />
                <Link href="#" variant="body2" sx={{ color: '#e88ba9', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                  Forgot password?
                </Link>
              </Box>

              <BakeryButton
                type="submit"
                fullWidth
              >
                Sign In
              </BakeryButton>

              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Typography variant="body2" sx={{ color: '#8c7077', fontWeight: 500 }}>
                  Craving something sweet?{' '}
                  <Link component={RouterLink} to="/register" sx={{ color: '#e88ba9', fontWeight: 800, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                    Create an account
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Box>

      <Snackbar
        message={error}
        autoHideDuration={4000}
        open={error !== ""}
        onClose={handleclose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        ContentProps={{
          sx: { background: '#fff', color: '#d46a89', fontWeight: 'bold', border: '1px solid #f4a2bc', boxShadow: '0 10px 30px rgba(212, 106, 137, 0.3)' }
        }}
      />
    </ThemeProvider>
  );
}

export default Login;